/**
 * Live DOM roots for Med Error / Fall dashboards so shell + ExportHub can PDF-capture
 * what the user sees (charts + filters), not a generated table.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

export type IncidentDashboardPdfExportApi = {
  registerMedErrorRoot: (el: HTMLElement | null) => void;
  registerFallRoot: (el: HTMLElement | null) => void;
  getMedErrorRoot: () => HTMLElement | null;
  getFallRoot: () => HTMLElement | null;
};

const IncidentDashboardPdfExportContext = createContext<IncidentDashboardPdfExportApi | null>(null);

export function IncidentDashboardPdfExportProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const medRef = useRef<HTMLElement | null>(null);
  const fallRef = useRef<HTMLElement | null>(null);

  const registerMedErrorRoot = useCallback((el: HTMLElement | null) => {
    medRef.current = el;
  }, []);

  const registerFallRoot = useCallback((el: HTMLElement | null) => {
    fallRef.current = el;
  }, []);

  const getMedErrorRoot = useCallback(() => medRef.current, []);
  const getFallRoot = useCallback(() => fallRef.current, []);

  const value = useMemo(
    () => ({
      registerMedErrorRoot,
      registerFallRoot,
      getMedErrorRoot,
      getFallRoot,
    }),
    [registerFallRoot, registerMedErrorRoot, getFallRoot, getMedErrorRoot],
  );

  return (
    <IncidentDashboardPdfExportContext.Provider value={value}>
      {children}
    </IncidentDashboardPdfExportContext.Provider>
  );
}

/** Optional: dashboards register even when provider is absent (tests). */
export function useOptionalIncidentDashboardPdfExport(): IncidentDashboardPdfExportApi | null {
  return useContext(IncidentDashboardPdfExportContext);
}

export function useIncidentDashboardPdfExport(): IncidentDashboardPdfExportApi {
  const v = useContext(IncidentDashboardPdfExportContext);
  if (!v) {
    throw new Error('useIncidentDashboardPdfExport requires IncidentDashboardPdfExportProvider');
  }
  return v;
}
